import { useState } from 'react'
import { PermissionButton } from '../components/Permissions.js'
import { useParams } from 'react-router-dom'
import { useApi, useAsync } from '../lib/useApi.js'
import { ApiError } from '../lib/api.js'
import type { CustomDomain } from '../lib/api.js'
import { CopyButton, PageHeader, Empty, ErrorBox, Loading, relative } from '../components/ui.js'
import { canCheck, explain, needsRecords, pillClass } from '../lib/domain-state.js'

/**
 * Connecting a domain the customer already owns.
 *
 * WHY THIS PAGE EARNS ITS EXISTENCE, WHERE DEPLOYMENTS MOSTLY DOES NOT
 *
 * Most of what the CLI does is better in the CLI. This is the exception.
 * The output of connecting a domain is a set of DNS records that have to be
 * retyped into somebody else's control panel, character for character - and
 * a TXT validation token is forty characters of base64 that nobody retypes
 * correctly. A browser can put a copy button next to each one. A terminal
 * cannot.
 *
 * THE MISTAKE THIS PAGE EXISTS TO PREVENT
 *
 * Connecting a domain does NOT make it work. The records are theirs to
 * create and nothing we do can create them, so somebody who reads
 * "connected" and closes the tab has a domain that will never resolve. The
 * word "connected" therefore appears nowhere until the certificate is live,
 * the records stay on screen for as long as they are outstanding, and the
 * status line says what is being waited for rather than just naming a state.
 */

/** One value with a copy button. The whole reason this page is not the CLI. */
function Copyable({ value }: { value: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="mono">{value}</span>
      <CopyButton value={value} />
    </span>
  )
}

/**
 * The records, as a DNS provider's form wants them.
 *
 * Every row says what it is FOR. A list of opaque TXT values is something
 * people delete six months later without knowing what breaks.
 */
function Records({ domain }: { domain: CustomDomain }) {
  const { records, warnings } = domain.instructions
  if (records.length === 0 && warnings.length === 0) return null

  return (
    <div style={{ marginTop: 12 }}>
      {records.length > 0 && (
        <>
          <p className="sub small" style={{ margin: '0 0 6px' }}>
            Create these at your DNS provider:
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Value</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={`${r.type}-${r.name}-${r.value}`}>
                    <td className="mono">{r.type}</td>
                    <td>
                      <Copyable value={r.name} />
                    </td>
                    <td>
                      <Copyable value={r.value} />
                    </td>
                    <td style={{ whiteSpace: 'normal', maxWidth: 260 }} className="sub small">
                      {r.purpose}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {warnings.map((w) => (
        <div className="notice" key={w} style={{ marginTop: 10 }}>
          <p className="sub small" style={{ margin: 0 }}>
            {w}
          </p>
        </div>
      ))}
    </div>
  )
}

export default function Domains() {
  const { id = '' } = useParams()
  const api = useApi()

  const project = useAsync(() => api.getProject(id), [api, id])
  const [reloadKey, setReloadKey] = useState(0)
  const domains = useAsync(() => api.listDomains(id), [api, id, reloadKey])

  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const p = project.data?.project

  async function add(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.addDomain(id, name.trim())
      setName('')
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not connect that domain.')
    } finally {
      setBusy(false)
    }
  }

  async function check(domainId: string) {
    setChecking(domainId)
    setError(null)
    try {
      await api.checkDomain(id, domainId)
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not check that domain.')
    } finally {
      setChecking(null)
    }
  }

  async function disconnect(d: CustomDomain) {
    if (busy) return
    if (
      !confirm(
        `Disconnect ${d.domain}? Its configuration will be removed. DNS records at your provider are not changed.`,
      )
    )
      return
    setError(null)
    setBusy(true)
    try {
      await api.deleteDomain(id, d.id)
      setReloadKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not disconnect that domain.')
    } finally {
      setBusy(false)
    }
  }

  if (project.loading) return <Loading />
  if (project.error) return <ErrorBox>{project.error}</ErrorBox>
  if (!p) return <Empty>Project not found.</Empty>

  const available = domains.data?.available ?? false
  const list = domains.data?.domains ?? []

  return (
    <>
      <PageHeader
        title="Custom domains"
        description="Connect a domain you own and follow its DNS and certificate setup."
      />
      <section className="panel" style={{ marginBottom: 24 }}>
        <h2>Default API endpoint</h2>
        {p.dataPlaneUrl ? (
          <div className="endpoint-line">
            <code>{p.dataPlaneUrl}</code>
            <CopyButton value={p.dataPlaneUrl} />
          </div>
        ) : (
          <p className="sub">Available after provisioning.</p>
        )}
        <p className="sub small">
          Certificate readiness does not verify end-to-end custom-hostname routing. Keep using the default
          endpoint until serving is verified.
        </p>
      </section>

      {error && <ErrorBox>{error}</ErrorBox>}

      {domains.loading ? (
        <Loading />
      ) : domains.error ? (
        <ErrorBox retry={domains.reload}>{domains.error}</ErrorBox>
      ) : !available ? (
        /* Not "no domains yet". Somebody waiting for one needs to know
             the difference between "none" and "not possible here". */
        <div className="card" style={{ padding: 16 }}>
          <p className="sub" style={{ margin: 0 }}>
            Custom domains are not available in this environment yet. Your project keeps serving on its own
            NimblyBase hostname.
          </p>
        </div>
      ) : (
        <>
          <form className="card" onSubmit={add} style={{ padding: 16, marginBottom: 16 }}>
            <div className="form-row">
              <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                <label htmlFor="domain">Domain you own</label>
                <input
                  id="domain"
                  value={name}
                  required
                  maxLength={253}
                  placeholder="app.example.com"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <PermissionButton
                permission="project:update"
                className="btn btn-primary"
                disabled={busy || !name.trim()}
              >
                {busy ? 'Connecting…' : 'Connect'}
              </PermissionButton>
            </div>
            <p className="sub small" style={{ marginTop: 10, marginBottom: 0 }}>
              You keep your nameservers. We give you records to add, and you can undo the whole thing by
              deleting them.
            </p>
          </form>

          {list.length === 0 ? (
            <Empty>No custom domains yet.</Empty>
          ) : (
            <div className="card">
              {list.map((d) => (
                <div className="row" key={d.id} style={{ display: 'block' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div className="row-main">
                      <div className="row-title">
                        {d.domain} <span className={pillClass(d)}>{explain(d)}</span>
                      </div>
                      <div className="row-sub">
                        {d.state === 'active' ? (
                          <>certificate ready · serving not verified · updated {relative(d.enteredAt)}</>
                        ) : (
                          <>waiting since {relative(d.enteredAt)}</>
                        )}
                      </div>
                      {d.stuck && d.state !== 'active' && (
                        <div className="row-sub" style={{ color: 'var(--amber, #b7791f)' }}>
                          This has been waiting a while. Check the records below match exactly — a trailing
                          dot or a missing subdomain is the usual cause.
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {canCheck(d) && (
                        <PermissionButton
                          permission="project:update"
                          className="btn"
                          disabled={checking !== null || busy}
                          onClick={() => check(d.id)}
                        >
                          {checking === d.id ? 'Checking…' : 'Check now'}
                        </PermissionButton>
                      )}
                      <PermissionButton
                        permission="project:update"
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={() => disconnect(d)}
                      >
                        Disconnect
                      </PermissionButton>
                    </div>
                  </div>
                  {/* Records stay on screen for as long as they are
                        outstanding. Hiding them behind a disclosure is how
                        somebody concludes there was nothing left to do. */}
                  {needsRecords(d) && <Records domain={d} />}
                </div>
              ))}
            </div>
          )}

          <p className="sub small" style={{ marginTop: 14 }}>
            Domains are checked automatically about once a minute. “Check now” only saves you the wait.
          </p>
        </>
      )}
    </>
  )
}
